import { reactive } from 'vue';
import { BinaryFilesByIdsDocument } from '@dott/graphql';
import type { useApolloClient } from '@vue/apollo-composable';

type ApolloClientLike = ReturnType<typeof useApolloClient>['client'];

export interface HydratedBinaryFile {
  status: 'pending' | 'ready' | 'error';
  url?: string;
  downloadUrl?: string;
  /** When the last error-driven refetch ran — see `refreshBinaryFileUrls`. */
  refreshedAt?: number;
}

/**
 * Signed URLs live for an hour, so a URL that fails within minutes of being
 * refetched is not expired — the file itself doesn't render (e.g. HEIC).
 */
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Presigned URLs must never be persisted in RichText payloads (they expire in
 * ~1h) — attachment nodes store only the BinaryFile id and resolve fresh URLs
 * here at render time. Requests are microtask-batched into a single
 * `binaryFilesByIds` query per flush so a document full of attachments costs
 * one round-trip.
 */
const cache = reactive(new Map<string, HydratedBinaryFile>());

let pendingIds = new Set<string>();
let flushScheduled = false;

const flush = async (client: ApolloClientLike): Promise<void> => {
  const ids = [...pendingIds];
  pendingIds = new Set();
  flushScheduled = false;

  if (ids.length === 0) {
    return;
  }

  try {
    const { data } = await client.query({
      query: BinaryFilesByIdsDocument,
      variables: { ids },
      fetchPolicy: 'network-only',
    });

    const found = new Set<string>();

    for (const file of data?.binaryFilesByIds ?? []) {
      found.add(file.id);
      // Keep the refetch time: fresh URLs alone don't prove the file renders
      // (e.g. HEIC, which browsers can't decode), and dropping it here turned
      // every load error into an endless refetch → re-render → error loop.
      cache.set(file.id, {
        status: 'ready',
        url: file.url ?? undefined,
        downloadUrl: file.downloadUrl ?? undefined,
        refreshedAt: cache.get(file.id)?.refreshedAt,
      });
    }

    for (const id of ids) {
      if (!found.has(id)) {
        const entry = cache.get(id);
        if (entry) {
          entry.status = 'error';
        }
      }
    }
  } catch {
    for (const id of ids) {
      const entry = cache.get(id);
      if (entry?.status === 'pending') {
        entry.status = 'error';
      }
    }
  }
};

const scheduleFlush = (client: ApolloClientLike): void => {
  if (flushScheduled) {
    return;
  }

  flushScheduled = true;
  queueMicrotask(() => void flush(client));
};

/**
 * Returns the (reactive) cache entry for a file id, queueing a batched fetch
 * on first access. Components read `.status` / `.url` / `.downloadUrl`.
 */
export const resolveBinaryFileUrls = (client: ApolloClientLike, fileId: string): HydratedBinaryFile => {
  const existing = cache.get(fileId);
  if (existing) {
    return existing;
  }

  const entry: HydratedBinaryFile = { status: 'pending' };
  cache.set(fileId, entry);
  pendingIds.add(fileId);
  scheduleFlush(client);

  return entry;
};

/** Seed the cache from a `createBinaryFile` response — skips the initial fetch. */
export const primeBinaryFileUrls = (
  fileId: string,
  urls: { url?: string | null; downloadUrl?: string | null },
): void => {
  cache.set(fileId, {
    status: 'ready',
    url: urls.url ?? undefined,
    downloadUrl: urls.downloadUrl ?? undefined,
  });
};

/**
 * Refetch a file's URLs after an image failed to load (presigned URL expired).
 * At most once per cooldown window, so an expiry always gets its refresh while
 * a file the browser can't decode stops after one. Returns false when refused.
 */
export const refreshBinaryFileUrls = (client: ApolloClientLike, fileId: string): boolean => {
  const entry = cache.get(fileId);

  if (!entry || (entry.refreshedAt !== undefined && Date.now() - entry.refreshedAt < REFRESH_COOLDOWN_MS)) {
    return false;
  }

  entry.refreshedAt = Date.now();
  entry.status = 'pending';
  pendingIds.add(fileId);
  scheduleFlush(client);

  return true;
};
